import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Pagination } from '../../components/ui/pagination';
import {
  Plus,
  FolderOpen,
  ArrowLeft,
  ChevronRight,
} from 'lucide-react';
import { Document, CategoryWithDocuments } from '../../lib/api';
import { FolderTile } from './FolderTile';
import {
  RenameFolderDialog,
  DeleteFolderDialog,
  CreateSubfolderDialog,
} from './FolderDialogs';
import { FolderAccessDialog } from './FolderAccessDialog';
import FolderDetailsDialog from './FolderDetailsDialog';
import { CategoryCard } from './CategoryCard';
import { SubLayout } from './FolderDocumentTiles';
import type { CanDeleteDocument } from './documentPermissions';

// Larger page-size for the polished tile grid — tiles are compact so we
// can show a full grid before paging kicks in.
const FOLDER_GRID_PAGE_SIZE = 12;

interface FolderViewProps {
  categories: CategoryWithDocuments[];
  searchTerm: string;
  onDownload: (doc: Document) => void;
  // Optional preset folder id pre-selects the upload target so an "Upload
  // here" action from inside a drilled-in folder lands in that folder.
  onUploadOpen: (presetCategoryId?: number) => void;
  onDelete?: (doc: Document) => void;
  // CR-8681: per-document Delete check (the server's rule, mirrored); absent
  // offers Delete on every document whenever onDelete is set.
  canDelete?: CanDeleteDocument;
  onEdit?: (doc: Document) => void;
  onPreview?: (doc: Document) => void;
  // CR-5330: Open the document details popup. When provided, clicking a
  // tile/row body opens the modal; clicking inline buttons or the kebab
  // menu still triggers their direct action (those handlers stop event
  // propagation so a row click can't fire alongside a button click).
  onOpenDetail?: (doc: Document) => void;
  subLayout: SubLayout;
  // Refresh the folder/document data after a folder mutation. Always
  // supplied so subfolder creation (allowed for staff / super admins who
  // are not customer admins) can refresh too.
  onFolderRefresh?: () => void;
  // True when the user may rename / delete folders (customer admins).
  canManageFolders?: boolean;
  // True when the user may upload into the folder they are viewing. Separate
  // from canManageFolders: suppliers upload documents but never rename or
  // delete folders, and overloading the folder-admin flag hid "Upload here"
  // from them entirely.
  canUpload?: boolean;
  // True when the user may create nested folders (customer users, staff,
  // super admins). Gates the "New subfolder" action; the backend
  // CanCreateSubfolder permission is the authoritative check.
  canCreateSubfolder?: boolean;
  // CR-7754: true on the customer side (admins and staff, plus Helix /
  // staff / super admins). Adds "Details" to every folder's menu and to the
  // open folder's header. Suppliers never get it; the backend 403s them.
  canViewFolderDetails?: boolean;
  // True when the user may decide which suppliers can see a folder
  // (customer admins). The backend IsHelixOrCustomerAdmin gate on the
  // assignment fields is the authoritative check.
  canAssignFolderAccess?: boolean;
  // Notifies the parent whether the user is currently drilled into a
  // folder. The page uses this to hide the global "Upload Document" button
  // while inside a folder — that button has no folder context, so showing
  // it next to the contextual "Upload here" reads as ambiguous.
  onFocusedFolderChange?: (focused: boolean) => void;
  // Multi-select for bulk permissions. When these are supplied the folder's
  // document list shows a checkbox per row and a selection bar with a
  // "Set permissions" action.
  selectedDocIds?: number[];
  onToggleDocSelected?: (id: number) => void;
  onSelectAllDocs?: (ids: number[]) => void;
  onClearSelectedDocs?: () => void;
  onOpenBulkPerms?: () => void;
}

export function FolderView({
  categories,
  searchTerm,
  onDownload,
  onUploadOpen,
  onDelete,
  canDelete,
  onEdit,
  onPreview,
  onOpenDetail,
  selectedDocIds,
  onToggleDocSelected,
  onSelectAllDocs,
  onClearSelectedDocs,
  onOpenBulkPerms,
  subLayout,
  onFolderRefresh,
  canManageFolders = false,
  canUpload = false,
  canCreateSubfolder = false,
  canViewFolderDetails = false,
  canAssignFolderAccess = false,
  onFocusedFolderChange,
}: FolderViewProps) {
  // Top-level pagination across root folders. 12 per page on desktop.
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryPageSize, setCategoryPageSize] = useState(FOLDER_GRID_PAGE_SIZE);

  // Drill-in navigation. A path of folder ids (root → … → current folder)
  // so arbitrarily-deep subfolders can be navigated with a breadcrumb.
  // Persisted in the URL (`?folder=4,43`) so a page refresh keeps you in
  // the folder you were viewing instead of resetting to the root grid.
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusedPath, setFocusedPath] = useState<Array<number | 'uncategorized'>>(() => {
    const raw = searchParams.get('folder');
    if (!raw) return [];
    return raw
      .split(',')
      .map((s) => (s === 'uncategorized' ? 'uncategorized' : Number(s)))
      .filter((x): x is number | 'uncategorized' => x === 'uncategorized' || Number.isFinite(x));
  });

  // Mirror the drill-in path into the URL (replace, so it doesn't pile up
  // history entries). Other query params (e.g. ?doc=) are preserved.
  useEffect(() => {
    const serialized = focusedPath.join(',');
    if ((searchParams.get('folder') ?? '') === serialized) return;
    const next = new URLSearchParams(searchParams);
    if (focusedPath.length) next.set('folder', serialized);
    else next.delete('folder');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedPath]);

  // Folder admin dialogs. Rename/delete are customer-admin only
  // (`onFolderRefresh` present); subfolder creation is gated separately by
  // `canCreateSubfolder`.
  const [renameTarget, setRenameTarget] = useState<CategoryWithDocuments | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CategoryWithDocuments | null>(null);
  const [subfolderTarget, setSubfolderTarget] = useState<CategoryWithDocuments | null>(null);
  const [accessTarget, setAccessTarget] = useState<CategoryWithDocuments | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<CategoryWithDocuments | null>(null);

  // The top-level grid shows root folders only — subfolders surface when
  // their parent folder is opened.
  const rootCategories = useMemo(
    () => categories.filter((c) => c.parent == null),
    [categories],
  );

  // Child folders of a given category id (empty for the Uncategorized
  // pseudo-folder, which has no real id).
  const childFoldersOf = (parentId: number | null | undefined) =>
    typeof parentId === 'number'
      ? categories.filter((c) => c.parent === parentId)
      : [];

  const totalCategoryPages = Math.max(1, Math.ceil(rootCategories.length / categoryPageSize));
  const safeCategoryPage = Math.min(categoryPage, totalCategoryPages);
  const pagedCategories = useMemo(() => {
    const start = (safeCategoryPage - 1) * categoryPageSize;
    return rootCategories.slice(start, start + categoryPageSize);
  }, [rootCategories, safeCategoryPage, categoryPageSize]);

  const focusedId = focusedPath.length ? focusedPath[focusedPath.length - 1] : null;
  // Resolve the id back to the live category so a refetch (rename/delete/
  // create elsewhere) reflects immediately.
  const focusedCategory = focusedId !== null
    ? categories.find((c) => (c.id ?? 'uncategorized') === focusedId) ?? null
    : null;

  // Tell the page whether we're drilled into a folder so it can hide the
  // global "Upload Document" button (which has no folder context) in favour
  // of the contextual "Upload here". Reset to false on unmount so switching
  // to list view restores the global button.
  useEffect(() => {
    onFocusedFolderChange?.(focusedCategory != null);
    return () => onFocusedFolderChange?.(false);
  }, [focusedCategory, onFocusedFolderChange]);

  // Folder mutation dialogs — kept in one place so they render in both the
  // grid view and the drilled-in view.
  const folderDialogs = (
    <>
      {renameTarget && (
        <RenameFolderDialog
          category={renameTarget}
          onClose={() => setRenameTarget(null)}
          onSaved={() => { setRenameTarget(null); onFolderRefresh?.(); }}
        />
      )}
      {deleteTarget && (
        <DeleteFolderDialog
          category={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); onFolderRefresh?.(); }}
        />
      )}
      {subfolderTarget && (
        <CreateSubfolderDialog
          parent={subfolderTarget}
          onClose={() => setSubfolderTarget(null)}
          onCreated={() => { setSubfolderTarget(null); onFolderRefresh?.(); }}
        />
      )}
      {accessTarget && (
        <FolderAccessDialog
          category={accessTarget}
          onClose={() => setAccessTarget(null)}
          onSaved={() => { setAccessTarget(null); onFolderRefresh?.(); }}
        />
      )}
      {detailsTarget && (
        <FolderDetailsDialog
          category={detailsTarget}
          onClose={() => setDetailsTarget(null)}
        />
      )}
    </>
  );

  // Per-folder action handlers passed to FolderTile menus. A handler is
  // only supplied when the user is entitled to that action.
  const folderActions = (category: CategoryWithDocuments) => ({
    onDetails:
      canViewFolderDetails && category.id ? () => setDetailsTarget(category) : undefined,
    onRename: canManageFolders && category.id ? () => setRenameTarget(category) : undefined,
    onDelete: canManageFolders && category.id ? () => setDeleteTarget(category) : undefined,
    onCreateSubfolder:
      canCreateSubfolder && category.id ? () => setSubfolderTarget(category) : undefined,
    onManageAccess:
      canAssignFolderAccess && category.id ? () => setAccessTarget(category) : undefined,
  });

  if (categories.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center text-center">
            <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'No documents found matching your search' : 'No documents yet'}
            </p>
            <Button className="mt-4" onClick={() => onUploadOpen()}>
              <Plus className="h-4 w-4 mr-2" />
              Upload your first document
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (focusedCategory) {
    const crumbs = focusedPath.map((id) => ({
      id,
      name: categories.find((c) => (c.id ?? 'uncategorized') === id)?.name ?? 'Folder',
    }));
    return (
      <div className="space-y-4">
        {/* Breadcrumb — "All folders" plus one crumb per path segment.
            An "Upload here" action sits on the right so a user inside a
            folder/subfolder uploads straight into it (the upload dialog
            opens with this folder pre-selected) rather than scrolling back
            to the page-top button and re-finding the folder. Only real
            folders (numeric id) can be a target — the Uncategorised pseudo-
            folder has no id and falls back to the plain upload dialog. */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap text-sm">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFocusedPath([])}
              className="-ml-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              All folders
            </Button>
            {crumbs.map((c, i) => (
              <span key={`${c.id}`} className="flex items-center gap-1">
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                {i < crumbs.length - 1 ? (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground hover:underline"
                    onClick={() => setFocusedPath(focusedPath.slice(0, i + 1))}
                  >
                    {c.name}
                  </button>
                ) : (
                  <span className="font-medium">{c.name}</span>
                )}
              </span>
            ))}
          </div>
          {canUpload && (
            <Button
              size="sm"
              onClick={() =>
                onUploadOpen(
                  typeof focusedCategory.id === 'number' ? focusedCategory.id : undefined,
                )
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              Upload here
            </Button>
          )}
        </div>
        <CategoryCard
          category={focusedCategory}
          childFolders={childFoldersOf(focusedCategory.id)}
          onOpenFolder={(id) => setFocusedPath([...focusedPath, id])}
          folderActions={folderActions}
          onDownload={onDownload}
          onDelete={onDelete}
          canDelete={canDelete}
          onEdit={onEdit}
          onPreview={onPreview}
          onOpenDetail={onOpenDetail}
          subLayout={subLayout}
          selectedDocIds={selectedDocIds}
          onToggleDocSelected={onToggleDocSelected}
          onSelectAllDocs={onSelectAllDocs}
          onClearSelectedDocs={onClearSelectedDocs}
          onOpenBulkPerms={onOpenBulkPerms}
        />
        {folderDialogs}
      </div>
    );
  }

  const categoryPager = totalCategoryPages > 1 ? (
    <Pagination
      currentPage={safeCategoryPage}
      totalPages={totalCategoryPages}
      totalCount={rootCategories.length}
      itemsPerPage={categoryPageSize}
      onPageChange={setCategoryPage}
      onPageSizeChange={(n) => { setCategoryPageSize(n); setCategoryPage(1); }}
      itemLabel="folders"
    />
  ) : null;

  return (
    <div className="space-y-4">
      {categoryPager}
      {subLayout === 'list' ? (
        <div className="divide-y rounded-md border bg-card">
          {pagedCategories.map((category) => (
            <FolderTile
              key={category.id ?? 'uncategorized'}
              category={category}
              variant="row"
              onOpen={() => setFocusedPath([category.id ?? 'uncategorized'])}
              {...folderActions(category)}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {pagedCategories.map((category) => (
            <FolderTile
              key={category.id ?? 'uncategorized'}
              category={category}
              onOpen={() => setFocusedPath([category.id ?? 'uncategorized'])}
              {...folderActions(category)}
            />
          ))}
        </div>
      )}
      {categoryPager}
      {folderDialogs}
    </div>
  );
}
